import { Injectable, signal, WritableSignal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, finalize, delay, tap } from 'rxjs/operators';
import { ExternalInvoice, ApiResponse } from '@app/types';

@Injectable({
    providedIn: 'root',
})
export class ExternalInvoiceService {
    private readonly http = inject(HttpClient);

    private _externalInvoices: WritableSignal<ExternalInvoice[]> = signal([]);
    public readonly externalInvoicesSignal = this._externalInvoices.asReadonly();

    private _isLoading: WritableSignal<boolean> = signal(true);
    public readonly isLoadingSignal = this._isLoading.asReadonly();

    // Contador para manejar múltiples peticiones concurrentes
    private _loadingCount = 0;

    private readonly API_URL = environment.API_URL;

    constructor() {
        // La carga inicial se hará desde el componente
    }

    private incrementLoading() {
        this._loadingCount++;
        this._isLoading.set(true);
    }

    private decrementLoading() {
        this._loadingCount--;
        if (this._loadingCount <= 0) {
            this._loadingCount = 0;
            this._isLoading.set(false);
        }
    }

    loadExternalInvoices(forceRefresh: boolean = true, silent: boolean = false): Observable<ExternalInvoice[]> {
        if (!silent) this.incrementLoading();
        const body = { filters: {}, limit: 5000, offset: 0 };
        return this.http.post<ApiResponse<ExternalInvoice>>(`${this.API_URL}/external-invoices/read`, body).pipe(
            map((response) => {
                const data = this.mapResponseToExternalInvoice(response.data);
                this._externalInvoices.set(data);
                return data;
            }),
            catchError((err) => {
                console.error('Error fetching external invoices from API', err);
                this._externalInvoices.set([]);
                return throwError(() => err);
            }),
            finalize(() => {
                if (!silent) this.decrementLoading();
            })
        );
    }

    loadExternalPreInvoices(silent: boolean = false): Observable<ExternalInvoice[]> {
        if (!silent) this.incrementLoading();
        const body = { filters: {}, limit: 5000, offset: 0 };
        return this.http.post<ApiResponse<ExternalInvoice>>(`${this.API_URL}/external-pre-invoices/read`, body).pipe(
            map((response) => {
                const data = this.mapResponseToExternalInvoice(response.data);
                this._externalInvoices.set(data);
                return data;
            }),
            catchError((err) => {
                console.error('Error fetching external pre-invoices', err);
                this._externalInvoices.set([]);
                return throwError(() => err);
            }),
            finalize(() => {
                if (!silent) this.decrementLoading();
            })
        );
    }

    loadExternalReferrals(silent: boolean = false): Observable<ExternalInvoice[]> {
        if (!silent) this.incrementLoading();
        const body = { filters: {}, limit: 5000, offset: 0 };
        return this.http.post<ApiResponse<ExternalInvoice>>(`${this.API_URL}/external-referrals/read`, body).pipe(
            map((response) => {
                const data = this.mapResponseToExternalInvoice(response.data);
                this._externalInvoices.set(data);
                return data;
            }),
            catchError((err) => {
                console.error('Error fetching external referrals', err);
                this._externalInvoices.set([]);
                return throwError(() => err);
            }),
            finalize(() => {
                if (!silent) this.decrementLoading();
            })
        );
    }

    private mapResponseToExternalInvoice(data: any[]): ExternalInvoice[] {
        return data.map((inv: any) => {
            const cleanAndParseFloat = (value: any): number | undefined => {
                if (value === null || value === undefined) return undefined;
                const cleanedString = String(value).replace(/[^0-9.-]+/g, "");
                return cleanedString ? parseFloat(cleanedString) : undefined;
            };

            return {
                id: inv.id,
                external_document_id: inv.external_document_id || inv.operator_document_id || '', // Backward compatibility
                documentFolio: inv.documentFolio || inv.document_folio || inv.folio_documento, // Folio del documento externo
                deleted: inv.deleted ?? false,
                uuid: inv.uuid || inv.cfdi_uuid,
                emisorNombre: inv.emisorNombre || inv.emisor_nombre,
                emisorRFC: inv.emisorRFC || inv.emisor_rfc,
                receptorNombre: inv.receptorNombre || inv.receptor_nombre,
                receptorRFC: inv.receptorRFC || inv.receptor_rfc,
                estatus: inv.estatus || 'Validación de Remisión',
                fechaEmision: inv.fechaEmision || inv.fecha_emision ? new Date(inv.fechaEmision || inv.fecha_emision) : undefined,
                fechaTimbrado: inv.fechaTimbrado || inv.fecha_timbrado ? new Date(inv.fechaTimbrado || inv.fecha_timbrado) : undefined,
                fechaVencimiento: inv.fechaVencimiento || inv.fecha_vencimiento ? new Date(inv.fechaVencimiento || inv.fecha_vencimiento) : undefined,
                folio: inv.folio,
                serie: inv.serie,
                moneda: inv.moneda,
                subtotal: cleanAndParseFloat(inv.subtotal),
                iva: cleanAndParseFloat(inv.iva),
                total: cleanAndParseFloat(inv.total),
                usoCFDI: inv.usoCFDI || inv.uso_cfdi,
                formaPago: inv.formaPago || inv.forma_pago,
                metodoPago: inv.metodoPago || inv.metodo_pago,

                // Campos de Remisión
                fechaRemision: inv.fechaRemision || inv.fecha_remision ? new Date(inv.fechaRemision || inv.fecha_remision) : undefined,
                folioRemision: inv.folioRemision || inv.folio_remision,
                folioContrato: inv.folioContrato || inv.folio_contrato,
                folioLicitacion: inv.folioLicitacion || inv.folio_licitacion,
                ordenSuministro: inv.ordenSuministro || inv.orden_suministro,

                // Datos de Proveedor
                proveedorNombre: inv.proveedorNombre || inv.proveedor_nombre,
                proveedorRFC: inv.proveedorRFC || inv.proveedor_rfc,
                proveedorFolio: inv.proveedorFolio || inv.proveedor_folio,

                statusHistory: (inv.statusHistory || []).map((h: any) => ({ ...h, fecha: h.fecha ? new Date(h.fecha) : undefined })),
                google_validation_status: inv.google_validation_status || 'PENDIENTE',
                google_metadata: inv.google_metadata || {},
                validation_discrepancies: inv.validation_discrepancies || [],

                // Campos de Origen Armstrong
                clave: inv.clave,
                entidadFederativa: inv.entidadFederativa || inv.entidad_federativa,
                almacen: inv.almacen,
                fechaPago: inv.fechaPago || inv.fecha_pago,
                fechaIngresoCobro: inv.fechaIngresoCobro || inv.fecha_ingreso_cobro,
                estatusPortal: inv.estatusPortal || inv.estatus_portal,

                seleccionada: false,
            } as ExternalInvoice;
        });
    }

    changeStatus(
        invoiceIds: string[],
        newStatus: string,
        observation: string,
        documentType: 'INVOICE' | 'REFERRAL' | 'PRE_INVOICE' = 'INVOICE'
    ): Observable<ApiResponse<ExternalInvoice>> {
        this.incrementLoading();
        const newHistoryEntry = {
            estatus: newStatus,
            fecha: new Date().toISOString(),
            observacion: observation,
            userId: localStorage.getItem('userId') || 'unknown'
        };

        const updates = invoiceIds.map(id => ({
            id: id,
            changes: {
                estatus: newStatus,
                statusHistory: newHistoryEntry
            }
        }));

        // Determinar el endpoint según el tipo de documento
        let endpoint: string;
        let reloadObservable: Observable<ExternalInvoice[]>;

        switch (documentType) {
            case 'REFERRAL':
                endpoint = `${this.API_URL}/external-referrals`;
                reloadObservable = this.loadExternalReferrals(true);
                break;
            case 'PRE_INVOICE':
                endpoint = `${this.API_URL}/external-pre-invoices`;
                reloadObservable = this.loadExternalPreInvoices(true);
                break;
            case 'INVOICE':
            default:
                endpoint = `${this.API_URL}/external-invoices`;
                reloadObservable = this.loadExternalInvoices(true);
                break;
        }

        return this.http.patch<ApiResponse<ExternalInvoice>>(endpoint, updates).pipe(
            switchMap(response => {
                // Recargar solo los datos del tipo correspondiente
                return reloadObservable.pipe(map(() => response));
            }),
            catchError(err => {
                console.error(`Error updating ${documentType} status`, err);
                return throwError(() => err);
            }),
            finalize(() => this.decrementLoading())
        );
    }

    updateExternalInvoices(
        updates: { id: string; changes: Partial<ExternalInvoice> }[],
        documentType: 'INVOICE' | 'REFERRAL' | 'PRE_INVOICE' = 'INVOICE'
    ): Observable<ApiResponse<ExternalInvoice>> {
        this.incrementLoading();

        let endpoint: string;
        let reloadObservable: Observable<ExternalInvoice[]>;

        switch (documentType) {
            case 'REFERRAL':
                endpoint = `${this.API_URL}/external-referrals`;
                reloadObservable = this.loadExternalReferrals(true);
                break;
            case 'PRE_INVOICE':
                endpoint = `${this.API_URL}/external-pre-invoices`;
                reloadObservable = this.loadExternalPreInvoices(true);
                break;
            case 'INVOICE':
            default:
                endpoint = `${this.API_URL}/external-invoices`;
                reloadObservable = this.loadExternalInvoices(true);
                break;
        }

        return this.http.patch<ApiResponse<ExternalInvoice>>(endpoint, updates).pipe(
            switchMap(response => reloadObservable.pipe(map(() => response))),
            catchError(err => {
                console.error(`Error updating ${documentType} fields`, err);
                return throwError(() => err);
            }),
            finalize(() => this.decrementLoading())
        );
    }

    seleccionarFactura(facturaId: string, seleccionada: boolean): void {
        this._externalInvoices.update(invoices =>
            invoices.map(inv => inv.id === facturaId ? { ...inv, seleccionada: seleccionada } : inv)
        );
    }

    seleccionarTodasLasFiltradas(seleccionar: boolean, idsFiltrados: string[]): void {
        this._externalInvoices.update(invoices =>
            invoices.map(inv => idsFiltrados.includes(inv.id) ? { ...inv, seleccionada: seleccionar } : inv)
        );
    }

    clearSelection(): void {
        this._externalInvoices.update(invoices =>
            invoices.map(inv => ({ ...inv, seleccionada: false }))
        );
    }

    getFacturasSeleccionadasIds(): string[] {
        return this._externalInvoices().filter((f: any) => f.seleccionada).map((f: any) => f.id);
    }

    createExternalInvoices(payload: any[]): Observable<any> {
        return this.http.post(`${this.API_URL}/external-invoices`, payload);
    }

    checkExistingKeys(uuids: string[], folios: string[]): Observable<{ uuids: string[], folios: string[] }> {
        return this.http.post<any>(`${this.API_URL}/external-invoices/check-duplicates`, {
            uuids,
            folios,
        }).pipe(
            map((response) => {
                const d = response.data;
                if (Array.isArray(d) && d.length > 0) return d[0];
                if (d && !Array.isArray(d)) return d;
                return { uuids: [], folios: [] };
            }),
            catchError((err) => {
                console.error('checkExistingKeys failed:', err);
                return of({ uuids: [], folios: [] });
            }),
        );
    }
}